import type {GridFilterOperator} from '@mui/x-data-grid-premium';
import {
    getGridBooleanOperators,
    getGridDateOperators,
    getGridNumericOperators,
    getGridStringOperators,
} from '@mui/x-data-grid-premium';
import {integerBetweenFilter} from "./IntegerBetweenFilter.tsx";
import {dateBetweenFilter} from "./DateBetweenFilter.tsx";

/**
 * Mirrors the buckets used by `FIELD_TYPE_FILTER_OPERATORS` in cat-material's
 * cat_config/filter_operators.py.
 */
export type FilterBucket = 'string' | 'number' | 'date' | 'boolean';

const DJ_TYPE_TO_BUCKET: Record<string, FilterBucket> = {
    CharField: 'string',
    TextField: 'string',
    DecimalField: 'number',
    FloatField: 'number',
    IntegerField: 'number',
    PositiveIntegerField: 'number',
    PositiveSmallIntegerField: 'number',
    PositiveBigIntegerField: 'number',
    DateTimeField: 'date',
    DateField: 'date',
    BooleanField: 'boolean',
};

export function mapDjTypeToBucket(dj_type: string | null): FilterBucket {
    return (dj_type && DJ_TYPE_TO_BUCKET[dj_type]) || 'string';
}

const OPERATOR_REGISTRY: Record<FilterBucket, Record<string, () => GridFilterOperator | undefined>> = {
    string: {
        contains: () => getGridStringOperators().find(o => o.value === 'contains'),
        equals: () => getGridStringOperators().find(o => o.value === 'equals'),
        starts_with: () => getGridStringOperators().find(o => o.value === 'startsWith'),
        ends_with: () => getGridStringOperators().find(o => o.value === 'endsWith'),
        is_empty: () => getGridStringOperators().find(o => o.value === 'isEmpty'),
        is_not_empty: () => getGridStringOperators().find(o => o.value === 'isNotEmpty'),
        is_any_of: () => getGridStringOperators().find(o => o.value === 'isAnyOf'),
    },
    number: {
        equals: () => getGridNumericOperators().find(o => o.value === '='),
        greater_than: () => getGridNumericOperators().find(o => o.value === '>'),
        greater_than_or_equal: () => getGridNumericOperators().find(o => o.value === '>='),
        less_than: () => getGridNumericOperators().find(o => o.value === '<'),
        less_than_or_equal: () => getGridNumericOperators().find(o => o.value === '<='),
        is_empty: () => getGridNumericOperators().find(o => o.value === 'isEmpty'),
        is_not_empty: () => getGridNumericOperators().find(o => o.value === 'isNotEmpty'),
        is_any_of: () => getGridNumericOperators().find(o => o.value === 'isAnyOf'),
        number_between: () => integerBetweenFilter,
    },
    date: {
        equals: () => getGridDateOperators().find(o => o.value === 'is'),
        greater_than: () => getGridDateOperators().find(o => o.value === 'after'),
        less_than: () => getGridDateOperators().find(o => o.value === 'before'),
        is_empty: () => getGridDateOperators().find(o => o.value === 'isEmpty'),
        is_not_empty: () => getGridDateOperators().find(o => o.value === 'isNotEmpty'),
        date_between: () => dateBetweenFilter,
    },
    boolean: {
        is_true_false: () => getGridBooleanOperators().find(o => o.value === 'is'),
    },
};

const warnedUnknownIds = new Set<string>();

export function getMuiFilterOperator(bucket: FilterBucket, operatorId: string): GridFilterOperator | undefined {
    const operator = OPERATOR_REGISTRY[bucket]?.[operatorId]?.();
    if (!operator && !warnedUnknownIds.has(`${bucket}:${operatorId}`)) {
        warnedUnknownIds.add(`${bucket}:${operatorId}`);
        console.warn(`No MUI filter operator found for bucket "${bucket}" and operator id "${operatorId}"`);
    }
    return operator;
}

export function getMuiFilterOperators(bucket: FilterBucket, operatorIds: string[]): GridFilterOperator[] {
    return operatorIds
        .map(id => getMuiFilterOperator(bucket, id))
        .filter((op): op is GridFilterOperator => Boolean(op));
}
